import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Filter, Info, RefreshCw, TriangleAlert } from "lucide-react";

// ── the shape the funnel service returns ────────────────────────────────────
interface Rate {
  numerator: number;
  denominator: number;
  percent: number | null;
  lowConfidence: boolean;
  estimate?: boolean;
  note?: string;
}
interface FunnelStage {
  id: string; label: string; count: number;
  fromPrevious: Rate | null; fromTop: Rate | null;
  estimate?: boolean; note?: string;
}
interface BreakdownRow {
  key: string; label: string;
  leads: number; sent: number; delivered: number; opened: number; clicked: number;
  bounced: number; complaints: number; unsubscribes: number; contactsReached: number;
  checksCompleted: number; signups: number; paid: number; revenueNet: number;
  conversion: Rate; bounceRate: Rate; complaintRate: Rate; clickRate: Rate;
  revenuePerThousandEmails: number | null;
}
interface Funnel {
  from: string; to: string; attributionDays: number; generatedAt: string;
  leads: { total: number; reachable: number; createdInRange: number; bounced: number; unsubscribed: number; complained: number; optedOut: number; suppressed: number };
  email: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; failed: number; complaints: number; unsubscribes: number; holdout: number; contactsReached: number; sendsWithoutContact: number };
  checks: { started: number; completed: number; captured: number; reported: number; offerShown: number; offerClicked: number; convertedOnCheck: number };
  outcomes: { signups: number; signupsAttributed: number; trials: number; paid: number; paidAttributed: number; revenueGross: number; revenueRefunds: number; revenueFees: number; revenueNet: number; revenueAttributedNet: number; newMrr: number };
  stages: FunnelStage[];
  checkDropOff: FunnelStage[];
  rates: Record<string, Rate>;
  economics: { revenuePerThousandEmails: number | null; revenuePerLeadReached: number | null; averageRevenuePerPaid: number | null; denominator: "delivered" | "sent" | "none" };
  byOffer: BreakdownRow[]; byWave: BreakdownRow[]; byGrade: BreakdownRow[];
  byRegion: BreakdownRow[]; bySchoolType: BreakdownRow[]; byCheck: BreakdownRow[];
  best: BreakdownRow[]; worst: BreakdownRow[]; lowConfidenceSegments: BreakdownRow[];
  retention: { converted: number; activeNow: number; churned: number; survival: Array<{ month: number; survival: number; atRisk: number; churned: number }>; cohorts: Array<{ month: string; size: number; active: number; retention: Array<number | null> }>; note: string };
  quality: Array<{ id: string; severity: "info" | "warn"; message: string }>;
}
interface Options {
  campaigns: Array<{ id: string; name: string }>;
  journeys: Array<{ id: string; name: string }>;
  offers: Array<{ code: string; count: number }>;
  sources: Array<{ source: string; count: number }>;
  grades: Array<{ grade: number; count: number }>;
  regions: Array<{ region: string; count: number }>;
}

// ── formatting ──────────────────────────────────────────────────────────────
const ANY = "__any__";
const int = (n: number) => (n ?? 0).toLocaleString();
const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? "–" : `J$${Math.round(cents / 100).toLocaleString()}`;

/** Jamaica time (UTC-5, no DST), so the page's days line up with the server's. */
function jmToday(offsetDays = 0): string {
  return new Date(Date.now() - 5 * 3600000 + offsetDays * 86400000).toISOString().slice(0, 10);
}

/**
 * A percentage that carries its own honesty. A rate over fewer than 30 events is marked
 * "low n" and anything the industry cannot measure exactly is marked "est.".
 */
function RateCell({ rate, className = "" }: { rate: Rate | null | undefined; className?: string }) {
  if (!rate) return <span className="text-muted-foreground">–</span>;
  if (rate.percent === null) {
    return <span className="text-muted-foreground" title={rate.note || "Nothing to measure against in this range"}>no data</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className="tabular-nums">{rate.percent.toFixed(1)}%</span>
      {rate.estimate ? (
        <Badge variant="outline" className="rounded-full px-1.5 py-0 text-[10px] font-medium border-amber-500/50 text-amber-700 dark:text-amber-400" title={rate.note || "Estimate"}>est.</Badge>
      ) : null}
      {rate.lowConfidence ? (
        <Badge variant="outline" className="rounded-full px-1.5 py-0 text-[10px] font-medium border-muted-foreground/40 text-muted-foreground" title={`Only ${int(rate.denominator)} events behind this rate. Under 30 it is noise, not a measurement.`}>low n</Badge>
      ) : null}
    </span>
  );
}

/** Raw dimension keys come back as the value itself ("5", "Portland", "Unknown"); give them a sentence. */
function gradeLabel(key: string) { return key === "Unknown" ? "Grade not known" : `Grade ${key}`; }
function schoolTypeLabel(key: string) { return key === "Unknown" ? "School type not known" : `${key} school`; }
function relabel(rows: BreakdownRow[] | undefined, fn: (k: string) => string): BreakdownRow[] {
  return (rows ?? []).map(r => ({ ...r, label: fn(r.key) }));
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode; tone?: "warn" }) {
  return (
    <div className={`rounded-xl border p-4 md:p-5 ${tone === "warn" ? "border-amber-400/60" : ""}`}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function SectionSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
    </div>
  );
}

// ── page ────────────────────────────────────────────────────────────────────
const RANGES = [
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "365", label: "Last 12 months" },
];

export default function AdminAcquisition() {
  const { toast } = useToast();
  const [range, setRange] = useState("30");
  const [campaignId, setCampaignId] = useState(ANY);
  const [journeyId, setJourneyId] = useState(ANY);
  const [offerCode, setOfferCode] = useState(ANY);
  const [source, setSource] = useState(ANY);
  const [grade, setGrade] = useState(ANY);
  const [region, setRegion] = useState(ANY);

  const to = jmToday();
  const from = jmToday(-(parseInt(range, 10) - 1));
  const qs = useMemo(() => {
    const p = new URLSearchParams({ from, to });
    if (campaignId !== ANY) p.set("campaignId", campaignId);
    if (journeyId !== ANY) p.set("journeyId", journeyId);
    if (offerCode !== ANY) p.set("offerCode", offerCode);
    if (source !== ANY) p.set("source", source);
    if (grade !== ANY) p.set("grade", grade);
    if (region !== ANY) p.set("region", region);
    return p.toString();
  }, [from, to, campaignId, journeyId, offerCode, source, grade, region]);

  const { data, isLoading, error, refetch, isFetching } = useQuery<Funnel>({
    queryKey: [`/api/admin/acquisition/funnel?${qs}`],
    queryFn: async () => (await apiRequest("GET", `/api/admin/acquisition/funnel?${qs}`)).json(),
  });
  const { data: options } = useQuery<Options>({
    queryKey: ["/api/admin/acquisition/options"],
    queryFn: async () => (await apiRequest("GET", "/api/admin/acquisition/options")).json(),
  });
  const snapshot = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/acquisition/snapshot", {})).json(),
    onSuccess: (r: any) => toast({ title: "Snapshot written", description: `${r.day}: ${r.paid} paid conversions, ${money(r.revenueNet)} net.` }),
    onError: (e: any) => toast({ title: "Snapshot failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const stages = data?.stages ?? [];
  const gradeRows = relabel(data?.byGrade, gradeLabel);
  const regionRows = relabel(data?.byRegion, k => (k === "Unknown" ? "Parish not known" : k));
  const schoolRows = relabel(data?.bySchoolType, schoolTypeLabel);
  const chartData = stages.map(s => ({ label: s.label, count: s.count, pct: s.fromPrevious?.percent ?? null }));
  const attributedShare = data ? data.outcomes.paid - data.outcomes.paidAttributed : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Acquisition</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Lead pool to paid subscriber, measured off the real tables: comms contacts, the send log, the free PEP
            Readiness Check and the Stripe mirrors. Last-touch attribution over {data?.attributionDays ?? 30} days;
            anything that cannot be tied to a message is reported as unattributed, never assigned.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40" data-testid="select-range"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching} title="Reload" data-testid="button-refresh">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-xl border">
        <CardContent className="flex flex-wrap items-center gap-2 p-4 md:p-5">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <FilterSelect label="Campaign" value={campaignId} onChange={setCampaignId} options={(options?.campaigns ?? []).map(c => ({ value: c.id, label: c.name }))} />
          <FilterSelect label="Journey" value={journeyId} onChange={setJourneyId} options={(options?.journeys ?? []).map(j => ({ value: j.id, label: j.name }))} />
          <FilterSelect label="Offer" value={offerCode} onChange={setOfferCode} options={(options?.offers ?? []).map(o => ({ value: o.code, label: `${o.code} (${int(o.count)})` }))} />
          <FilterSelect label="Lead source" value={source} onChange={setSource} options={(options?.sources ?? []).map(s => ({ value: s.source, label: `${s.source} (${int(s.count)})` }))} />
          <FilterSelect label="Grade" value={grade} onChange={setGrade} options={(options?.grades ?? []).map(g => ({ value: String(g.grade), label: `Grade ${g.grade} (${int(g.count)})` }))} />
          <FilterSelect label="Parish" value={region} onChange={setRegion} options={(options?.regions ?? []).map(r => ({ value: r.region, label: `${r.region} (${int(r.count)})` }))} />
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">{from} to {to}</span>
            <Button variant="outline" size="sm" onClick={() => snapshot.mutate()} disabled={snapshot.isPending} data-testid="button-snapshot">
              Snapshot yesterday
            </Button>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="rounded-xl border border-destructive/60">
          <CardContent className="p-4 md:p-5 text-sm">
            <p className="font-medium text-destructive">Could not load the funnel</p>
            <p className="text-muted-foreground">{(error as Error)?.message}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Headline numbers */}
      {isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Leads reachable" value={int(data.leads.reachable)} sub={`${int(data.leads.total)} in the pool, counted now`} />
          <Tile label="Emails sent" value={int(data.email.sent)} sub={<span>to {int(data.email.contactsReached)} people, <RateCell rate={data.rates.deliveryRate} /> delivered</span>} />
          <Tile label="Paid conversions" value={int(data.outcomes.paid)} sub={`${int(data.outcomes.paidAttributed)} attributed, ${int(attributedShare)} unattributed`} tone={attributedShare > data.outcomes.paidAttributed ? "warn" : undefined} />
          <Tile label="Net revenue" value={money(data.outcomes.revenueNet)} sub={`${money(data.outcomes.revenueAttributedNet)} attributable, ${money(data.outcomes.newMrr)} new MRR`} />
          <Tile label="Revenue per 1,000 emails" value={money(data.economics.revenuePerThousandEmails)} sub={`per 1,000 ${data.economics.denominator === "sent" ? "sent (no delivery webhooks)" : "delivered"}`} />
          <Tile label="Signups" value={int(data.outcomes.signups)} sub={<span><RateCell rate={data.rates.signupAttributionRate} /> tied to a send</span>} />
          <Tile label="Readiness checks" value={int(data.checks.started)} sub={<span><RateCell rate={data.rates.checkCompletionRate} /> finished, {int(data.checks.captured)} gave an email</span>} />
          <Tile label="Average revenue per payer" value={money(data.economics.averageRevenuePerPaid)} sub={`${int(data.outcomes.trials)} trials started`} />
        </div>
      )}

      {/* Data quality */}
      {data?.quality?.length ? (
        <Card className="rounded-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base md:text-lg font-semibold">What these numbers can and cannot say</CardTitle>
            <CardDescription>Read this before quoting anything on this page.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 p-4 md:p-5 pt-0">
            {data.quality.map(q => (
              <div key={q.id} className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${q.severity === "warn" ? "border-amber-400/60" : ""}`}>
                {q.severity === "warn" ? <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                <span className={q.severity === "warn" ? "" : "text-muted-foreground"}>{q.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Funnel */}
      <Card className="rounded-xl border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg font-semibold">The funnel</CardTitle>
          <CardDescription>
            Each stage against the one above it. The lead pool is a live count; every other stage is scoped to the date
            range. Sent, delivered, opened and clicked count messages rather than people, so they carry no share of the
            pool: that column is blank for them on purpose.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-5 pt-0">
          {isLoading || !data ? <SectionSkeleton rows={8} /> : (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="h-[380px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="hsl(var(--border))" />
                    <XAxis type="number" scale="sqrt" hide />
                    <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      {chartData.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" fillOpacity={1 - i * 0.07} />)}
                      <LabelList dataKey="count" position="right" className="fill-foreground" formatter={(v: any) => int(Number(v))} style={{ fontSize: 11 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="mt-1 text-center text-xs text-muted-foreground">Bar length uses a square-root scale so the small stages stay visible.</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Stage</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">From previous</TableHead>
                      <TableHead className="text-right">From pool</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stages.map(s => (
                      <TableRow key={s.id} data-testid={`row-stage-${s.id}`}>
                        <TableCell className="font-medium" title={s.note}>{s.label}</TableCell>
                        <TableCell className="text-right tabular-nums">{int(s.count)}</TableCell>
                        <TableCell className="text-right"><RateCell rate={s.fromPrevious} className="justify-end" /></TableCell>
                        <TableCell className="text-right"><RateCell rate={s.fromTop} className="justify-end" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Offers */}
      <Card className="rounded-xl border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg font-semibold">Offers, ranked by paid conversions</CardTitle>
          <CardDescription>
            An offer owns a signup or a payment only when its message was the last one to reach that person inside the
            attribution window. Everything else sits in the Unattributed row.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-5 pt-0">
          {isLoading || !data ? <SectionSkeleton /> : (
            <BreakdownTable
              rows={data.byOffer}
              firstHeader="Offer code"
              columns={["sent", "delivered", "clicked", "checks", "signups", "paid", "revenue", "conversion", "rpm"]}
              empty="No offer codes have been stamped on a send or a readiness check yet."
            />
          )}
        </CardContent>
      </Card>

      {/* Waves */}
      <Card className="rounded-xl border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg font-semibold">Waves</CardTitle>
          <CardDescription>
            A wave is what the sender stamps on the message; without a stamp it is the day the message went out.
            Watch bounce and complaint rates here. Reputation is the constraint on how fast this list can be worked.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-5 pt-0">
          {isLoading || !data ? <SectionSkeleton /> : (
            <BreakdownTable
              rows={[...data.byWave].sort((a, b) => a.key.localeCompare(b.key))}
              firstHeader="Wave"
              columns={["sent", "delivered", "bounce", "complaint", "clicked", "signups", "paid", "revenue", "conversion"]}
              empty="Nothing has been sent in this range."
            />
          )}
        </CardContent>
      </Card>

      {/* Readiness check drop-off */}
      <Card className="rounded-xl border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg font-semibold">Readiness check drop-off</CardTitle>
          <CardDescription>Where people leave the free PEP Readiness Check, and whether taking it changes the odds.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 p-4 md:p-5 pt-0 lg:grid-cols-2">
          {isLoading || !data ? <SectionSkeleton rows={7} /> : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Step</TableHead>
                      <TableHead className="text-right">People</TableHead>
                      <TableHead className="text-right">Kept</TableHead>
                      <TableHead className="text-right">Of starters</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.checkDropOff.map(s => (
                      <TableRow key={s.id} data-testid={`row-check-${s.id}`}>
                        <TableCell className="font-medium">{s.label}</TableCell>
                        <TableCell className="text-right tabular-nums">{int(s.count)}</TableCell>
                        <TableCell className="text-right"><RateCell rate={s.fromPrevious} className="justify-end" /></TableCell>
                        <TableCell className="text-right"><RateCell rate={s.fromTop} className="justify-end" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="overflow-x-auto">
                <BreakdownTable
                  rows={data.byCheck}
                  firstHeader="Took the check"
                  columns={["signups", "paid", "revenue", "conversion"]}
                  empty="No signups from either group in this range."
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  This is an observed split, not an experiment: people who choose to take a diagnostic are already more
                  interested. Read it as a signal to test, not as proof the check caused the sale.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Segments */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base md:text-lg font-semibold">Best segments</CardTitle>
            <CardDescription>Grade, parish and school type, ranked by paid conversion. Segments with fewer than 30 people reached are excluded.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-5 pt-0">
            {isLoading || !data ? <SectionSkeleton /> : (
              <BreakdownTable rows={data.best} firstHeader="Segment" columns={["reached", "signups", "paid", "revenue", "conversion"]} empty="No segment yet has 30 people reached, so nothing here can be ranked honestly." />
            )}
          </CardContent>
        </Card>
        <Card className="rounded-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base md:text-lg font-semibold">Worst segments</CardTitle>
            <CardDescription>Same rule, bottom of the list. These are the ones to stop paying attention to, or to change the message for.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-5 pt-0">
            {isLoading || !data ? <SectionSkeleton /> : (
              <BreakdownTable rows={data.worst} firstHeader="Segment" columns={["reached", "signups", "paid", "revenue", "conversion"]} empty="Nothing to show yet." />
            )}
          </CardContent>
        </Card>
      </div>

      {data?.lowConfidenceSegments?.length ? (
        <Card className="rounded-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base md:text-lg font-semibold">Too thin to rank</CardTitle>
            <CardDescription>These segments have a conversion rate, but under 30 people reached it is noise. Shown so they are not invisible.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-5 pt-0">
            <BreakdownTable rows={data.lowConfidenceSegments} firstHeader="Segment" columns={["reached", "signups", "paid", "revenue", "conversion"]} empty="" />
          </CardContent>
        </Card>
      ) : null}

      {/* Grade / parish / school type detail */}
      <div className="grid gap-4 lg:grid-cols-3">
        {([["Grade", data ? gradeRows : undefined], ["Parish", data ? regionRows : undefined], ["School type (derived from the school name)", data ? schoolRows : undefined]] as Array<[string, BreakdownRow[] | undefined]>).map(([title, rows]) => (
          <Card key={title} className="rounded-xl border">
            <CardHeader className="pb-2"><CardTitle className="text-base md:text-lg font-semibold">{title}</CardTitle></CardHeader>
            <CardContent className="p-4 md:p-5 pt-0">
              {isLoading || !rows ? <SectionSkeleton rows={4} /> : (
                <BreakdownTable rows={rows} firstHeader={title} columns={["leads", "reached", "paid", "conversion"]} empty="No leads carry this field." />
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Retention of the converted */}
      <Card className="rounded-xl border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg font-semibold">Do converted leads stay?</CardTitle>
          <CardDescription>{data?.retention.note}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-5 pt-0">
          {isLoading || !data ? <SectionSkeleton rows={4} /> : data.retention.converted === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No lead has paid yet, so there is nothing to survive.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Tile label="Leads who have ever paid" value={int(data.retention.converted)} />
                <Tile label="Still paying" value={int(data.retention.activeNow)} />
                <Tile label="Churned" value={int(data.retention.churned)} />
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      {data.retention.survival.map(s => <TableHead key={s.month} className="text-right">M{s.month}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Still subscribed</TableCell>
                      {data.retention.survival.map(s => (
                        <TableCell key={s.month} className="text-right tabular-nums" title={`${s.atRisk} at risk, ${s.churned} churned`}>
                          {s.survival < 0 ? <span className="text-muted-foreground" title="Fewer than 5 subscribers still at risk: too thin to report">low n</span> : `${Math.round(s.survival * 1000) / 10}%`}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {data.retention.cohorts.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cohort</TableHead>
                        <TableHead className="text-right">Size</TableHead>
                        {[1, 2, 3, 4, 5, 6].map(m => <TableHead key={m} className="text-right">M{m}</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.retention.cohorts.map(c => (
                        <TableRow key={c.month}>
                          <TableCell className="font-medium tabular-nums">{c.month}</TableCell>
                          <TableCell className="text-right tabular-nums">{int(c.size)}</TableCell>
                          {Array.from({ length: 6 }).map((_, i) => (
                            <TableCell key={i} className="text-right tabular-nums">
                              {c.retention[i] === null || c.retention[i] === undefined ? <span className="text-muted-foreground">–</span> : `${c.retention[i]}%`}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="mt-2 text-xs text-muted-foreground">Blank cells are months the cohort has not lived through yet, not zeros.</p>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {data ? (
        <p className="text-xs text-muted-foreground">
          Built {new Date(data.generatedAt).toLocaleString()}. Revenue and subscription state come from the Stripe
          mirrors kept by the metrics service; Stripe is not called from this page.
        </p>
      ) : null}
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────
function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[9rem]" data-testid={`select-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{label}: any</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

type Column = "leads" | "reached" | "sent" | "delivered" | "opened" | "clicked" | "bounce" | "complaint" | "checks" | "signups" | "paid" | "revenue" | "conversion" | "rpm";
const COLUMN_LABEL: Record<Column, string> = {
  leads: "Leads", reached: "Reached", sent: "Sent", delivered: "Delivered", opened: "Opened", clicked: "Clicked",
  bounce: "Bounce", complaint: "Complaint", checks: "Checks done", signups: "Signups", paid: "Paid",
  revenue: "Net revenue", conversion: "Conversion", rpm: "Rev / 1k emails",
};

function BreakdownTable({ rows, firstHeader, columns, empty }: { rows: BreakdownRow[]; firstHeader: string; columns: Column[]; empty: string }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  const cell = (r: BreakdownRow, c: Column) => {
    switch (c) {
      case "leads": return <span className="tabular-nums">{int(r.leads)}</span>;
      case "reached": return <span className="tabular-nums">{int(r.contactsReached)}</span>;
      case "sent": return <span className="tabular-nums">{int(r.sent)}</span>;
      case "delivered": return <span className="tabular-nums">{int(r.delivered)}</span>;
      case "opened": return <span className="tabular-nums">{int(r.opened)}</span>;
      case "clicked": return <span className="tabular-nums">{int(r.clicked)}</span>;
      case "bounce": return <RateCell rate={r.bounceRate} className="justify-end" />;
      case "complaint": return <RateCell rate={r.complaintRate} className="justify-end" />;
      case "checks": return <span className="tabular-nums">{int(r.checksCompleted)}</span>;
      case "signups": return <span className="tabular-nums">{int(r.signups)}</span>;
      case "paid": return <span className="font-medium tabular-nums">{int(r.paid)}</span>;
      case "revenue": return <span className="font-medium tabular-nums">{money(r.revenueNet)}</span>;
      case "conversion": return <RateCell rate={r.conversion} className="justify-end" />;
      case "rpm": return <span className="tabular-nums">{money(r.revenuePerThousandEmails)}</span>;
    }
  };
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{firstHeader}</TableHead>
            {columns.map(c => <TableHead key={c} className="text-right whitespace-nowrap">{COLUMN_LABEL[c]}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.key} data-testid={`row-${r.key}`}>
              <TableCell className="font-medium">
                {r.label}
                {r.key === "Unattributed" ? (
                  <Badge variant="outline" className="ml-2 rounded-full px-1.5 py-0 text-[10px]" title="No send could be tied to these people inside the attribution window">no send found</Badge>
                ) : null}
              </TableCell>
              {columns.map(c => <TableCell key={c} className="text-right">{cell(r, c)}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
